import type { FilterService } from '../shared/filter.service';
import type { SearchService } from '../shared/search.service';
import type { UiStateService } from '../shared/ui-state.service';
import type { ScrollPositionService } from '../shared/scroll-position.service';

export const GROWERS_LIST_SNAPSHOT_KEY = 'dgag_growers_list_snapshot';
export const GROWERS_EXPECT_RESTORE_KEY = 'dgag_growers_expect_restore';

/** Desktop "Show rows" values — must stay in sync with desktop-table-templates growers dropdown */
const GROWERS_DESKTOP_SHOW_ROWS_OPTIONS = new Set([10, 25, 50, 100, 150, 200, 250]);

export function normalizeGrowersDesktopShowRows(size: number): number {
  if (GROWERS_DESKTOP_SHOW_ROWS_OPTIONS.has(size)) {
    return size;
  }
  return 10;
}

export interface GrowersListSnapshot {
  tab: 'pending' | 'outgoing' | 'completed';
  filters: Record<string, string[]>;
  searchQuery: string;
  currentPage: number;
  itemsPerPage: number;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  passportId: string;
  scrollY?: number;
}

export interface LeaveGrowersPersonaListStateDeps {
  filterService: FilterService;
  searchService: SearchService;
  ui: UiStateService;
  scrollPosition: ScrollPositionService;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function isFiltersRecord(value: unknown): value is Record<string, string[]> {
  if (!isRecord(value)) {
    return false;
  }
  for (const v of Object.values(value)) {
    if (!isStringArray(v)) {
      return false;
    }
  }
  return true;
}

function isGrowersListSnapshot(value: unknown): value is GrowersListSnapshot {
  if (!isRecord(value)) {
    return false;
  }
  const tab = value['tab'];
  if (tab !== 'pending' && tab !== 'outgoing' && tab !== 'completed') {
    return false;
  }
  if (!isFiltersRecord(value['filters'])) {
    return false;
  }
  if (typeof value['searchQuery'] !== 'string') {
    return false;
  }
  if (typeof value['currentPage'] !== 'number' || !Number.isFinite(value['currentPage'])) {
    return false;
  }
  if (typeof value['itemsPerPage'] !== 'number' || !Number.isFinite(value['itemsPerPage'])) {
    return false;
  }
  if (typeof value['sortColumn'] !== 'string') {
    return false;
  }
  const dir = value['sortDirection'];
  if (dir !== 'asc' && dir !== 'desc') {
    return false;
  }
  if (typeof value['passportId'] !== 'string') {
    return false;
  }
  const scrollY = value['scrollY'];
  if (scrollY !== undefined && (typeof scrollY !== 'number' || !Number.isFinite(scrollY))) {
    return false;
  }
  return true;
}

export function peekGrowersExpectRestore(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(GROWERS_EXPECT_RESTORE_KEY) === '1';
  } catch {
    return false;
  }
}

export function hasGrowersListSnapshot(): boolean {
  try {
    const raw = globalThis.sessionStorage?.getItem(GROWERS_LIST_SNAPSHOT_KEY);
    return typeof raw === 'string' && raw.length > 0;
  } catch {
    return false;
  }
}

export function clearGrowersListRestoreState(): void {
  try {
    globalThis.sessionStorage?.removeItem(GROWERS_LIST_SNAPSHOT_KEY);
    globalThis.sessionStorage?.removeItem(GROWERS_EXPECT_RESTORE_KEY);
  } catch {
    // sessionStorage may be unavailable (SSR / private mode)
  }
}

export function saveGrowersListSnapshot(snapshot: GrowersListSnapshot): void {
  try {
    globalThis.sessionStorage?.setItem(GROWERS_LIST_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore
  }
}

export function parseGrowersListSnapshot(): GrowersListSnapshot | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(GROWERS_LIST_SNAPSHOT_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isGrowersListSnapshot(parsed)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function leaveGrowersPersonaListState(deps: LeaveGrowersPersonaListStateDeps): void {
  clearGrowersListRestoreState();
  deps.filterService.clear();
  deps.searchService.clear();
  deps.ui.setSearchOpen(false);
  deps.ui.setFiltersOpen(false);
  deps.ui.setStatusSelected('pending');
  deps.scrollPosition.clearScrollPosition('/growers');
}
